import { useEffect, useState } from "react";

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Typography,
  useTheme
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";

import CsvAttentionGroups from "./CsvAttentionGroups";
import CsvErroredRows from "./CsvErroredRows";
import CsvFailedLines from "./CsvFailedLines";
import CsvLinesBrowser from "./CsvLinesBrowser";
import SmartUploadSlideoutPrompt from "./SmartUploadSlideoutPrompt";

import { lineKeyFor } from "./reviewLineGates";
import {
  ACTIVE_RUN_STATUSES,
  TERMINAL_RUN_STATUSES,
  useBulkCommitRun,
  useCsvReviewSummary
} from "./useCsvReview";
import {
  cancelCsvBulkCommit,
  commitSmartUploadLines,
  startCsvBulkCommit,
  updateCsvLineGroup
} from "../../../helpers/smartUpload";
import usePlatformLayoutContext from "../../../helpers/contexts/platformLayout";

const READY_COUNT_CHANGED_COPY =
  "The number of ready lines changed while you were reviewing — check the new count and confirm again.";

const TITLES = {
  transactions_csv: "Review uploaded transactions",
  shipping_transactions: "Review uploaded shipments",
  bom_products_csv: "Review imported products",
  utilities_csv: "Review uploaded utility usage",
  office_csv: "Review uploaded office usage"
};

// Ticket DlAT2n1ITtjB6BA9ntuJ: the confirm button reads "ready products" for
// bom_products_csv instead of the generic "ready lines" noun.
const READY_NOUNS = {
  bom_products_csv: ["ready product", "ready products"]
};

const readyNounFor = (documentFamily) =>
  READY_NOUNS[documentFamily] || ["ready line", "ready lines"];

const plural = (count, singular, pluralWord) =>
  `${count} ${count === 1 ? singular : pluralWord}`;

const summaryCaption = ({
  readyCount,
  attentionCount,
  erroredRowCount,
  committedCount,
  failedCount = 0,
  inProgressCount = 0
}) =>
  [
    `${readyCount} ready`,
    `${attentionCount} need attention`,
    `${erroredRowCount} couldn't be read`,
    !!failedCount && `${failedCount} couldn't be recorded`,
    !!inProgressCount && `${inProgressCount} being recorded`,
    `${committedCount} recorded`
  ]
    .filter(Boolean)
    .join(" · ");

// A transactions/shipping task that finished before review existed was
// auto-imported, so it has no lines, no errored rows and nothing to count.
const isPreReviewImport = (task, summary) =>
  task.status === "finished" &&
  !!summary &&
  !summary.readyCount &&
  !summary.attentionCount &&
  !summary.committedCount &&
  !summary.erroredRowCount &&
  !summary.failedCount &&
  !summary.inProgressCount;

const runProgressCount = ({ committed = 0, already_committed: already = 0 }) =>
  committed + already;

const runProgressText = (run) =>
  run.status === "tagging"
    ? "Getting your ready lines lined up to confirm… · You can close this window"
    : `${runProgressCount(run)} of ${
        run.line_count || 0
      } confirmed · You can close this window`;

const RunProgress = ({ run, onCancel, isCancelling }) => {
  const done = runProgressCount(run);
  const total = run.status === "tagging" ? 0 : run.line_count || 0;
  return (
    <Box sx={{ my: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Typography variant="body2">{runProgressText(run)}</Typography>
        <Button size="small" disabled={isCancelling} onClick={onCancel}>
          Cancel
        </Button>
      </Box>
      <LinearProgress
        variant={total ? "determinate" : "indeterminate"}
        value={total ? Math.min(100, (done / total) * 100) : 0}
      />
    </Box>
  );
};

const RUN_OUTCOMES = {
  completed: {
    severity: "success",
    text: (run) =>
      `${plural(runProgressCount(run), "line", "lines")} confirmed.`
  },
  cancelled: {
    severity: "info",
    text: (run) =>
      `Confirmation cancelled — ${plural(
        runProgressCount(run),
        "line was",
        "lines were"
      )} confirmed before you cancelled; the rest are back in review.`
  },
  failed: {
    severity: "error",
    text: () => "We couldn't start confirming — nothing was recorded."
  },
  stalled: {
    severity: "warning",
    text: (run) =>
      `Confirmation stopped after repeated delays — ${plural(
        runProgressCount(run),
        "line",
        "lines"
      )} confirmed; the rest are back in review. Try again.`
  }
};

const GROUP_ERROR_COPY = "We couldn't apply that to these lines.";

// csv-update-line-group handles at most 500 lines per call and reports what
// is left; the same decision is re-sent until nothing remains. A call that
// makes no progress stops the loop instead of spinning.
const applyGroupInRounds = async ({
  request,
  onProgress,
  rejected = [],
  previousRemaining = Infinity
}) => {
  const response = await updateCsvLineGroup(request);
  if (!response || response.error) {
    return { error: response || {}, rejected };
  }
  const allRejected = [...rejected, ...(response.rejected || [])];
  const remaining = response.remainingCount || 0;
  const skippedOverridden = response.skippedOverriddenCount || 0;
  if (!remaining) {
    return { rejected: allRejected, skippedOverridden };
  }
  if (remaining >= previousRemaining) {
    return {
      error: {
        message: `${GROUP_ERROR_COPY} ${remaining} lines weren't updated.`
      },
      rejected: allRejected
    };
  }
  onProgress(remaining);
  return applyGroupInRounds({
    request,
    onProgress,
    rejected: allRejected,
    previousRemaining: remaining
  });
};

// The summary reports the task's status itself, so a retry that sends the
// file back to "running" shows here without waiting on the task listener.
const isStillReading = (task, summary) =>
  task.status === "uploading" || summary?.taskStatus === "running";

const reviewNoticeFor = ({ task, summary, recordCount }) => {
  if (isStillReading(task, summary)) {
    return "Still reading your file — you'll be able to review when it's done.";
  }
  if (isPreReviewImport(task, summary)) {
    return `Imported before review was available — ${plural(
      recordCount,
      "record",
      "records"
    )}.`;
  }
  if (task.status === "finished") {
    return "This file is finished — every line has been reviewed.";
  }
  return null;
};

// Smart Upload review for a migrated transactions or shipping CSV (#393).
// A file can hold thousands of rows, so review is by exception: the reviewer
// resolves the (grouped) lines that need a person, spot-checks the rest in a
// paged browser, and confirms every ready line with one click that runs
// server-side in the background. Nothing here subscribes to a row's lines --
// the task doc and the active run doc are the only listeners.
const SmartUploadCsvReview = ({
  companyId,
  taskDocId,
  task,
  onClose,
  vendorOptions,
  closedYears,
  isPhysicalContext,
  contextEntityName
}) => {
  const { palette } = useTheme();
  const { activateSnackbar } = usePlatformLayoutContext() || {};
  const { document_family: documentFamily } = task;

  const {
    summary,
    attention,
    attentionError,
    loadError,
    refresh,
    refreshKey,
    canReview
  } = useCsvReviewSummary({ companyId, taskDocId, taskStatus: task.status });

  const [startedRunId, setStartedRunId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [isStarting, setIsStarting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [isSlideoutConfirmed, setIsSlideoutConfirmed] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [commitResults, setCommitResults] = useState({});
  const [committingKeys, setCommittingKeys] = useState([]);
  const [groupProgress, setGroupProgress] = useState(null);

  const runId =
    startedRunId || task.active_bulk_run_id || summary?.activeBulkRunId || null;
  const { run, runError } = useBulkCommitRun({ companyId, taskDocId, runId });
  const isRunActive = run
    ? ACTIVE_RUN_STATUSES.includes(run.status)
    : !!runId && task.status === "bulk_committing";
  const runStatus = run?.status;

  useEffect(() => {
    if (!TERMINAL_RUN_STATUSES.includes(runStatus)) {
      return;
    }
    refresh();
  }, [runStatus, refresh]);

  const showError = (response, fallback) => {
    const message = response?.message || fallback;
    if (!activateSnackbar) {
      return setNotice({ severity: "error", text: message });
    }
    return activateSnackbar({ message, alert: "error" });
  };

  const requestWithSlideout = (action) => {
    if (isPhysicalContext && !isSlideoutConfirmed) {
      return setPendingAction(() => action);
    }
    return action();
  };

  const confirmSlideout = () => {
    const action = pendingAction;
    setIsSlideoutConfirmed(true);
    setPendingAction(null);
    return action();
  };

  const applyToGroup = async (groupKey, fields) => {
    const { error, rejected, skippedOverridden } = await applyGroupInRounds({
      request: { companyId, taskDocId, groupKey, fields },
      onProgress: (remaining) => setGroupProgress({ groupKey, remaining })
    });
    setGroupProgress(null);
    await refresh();
    if (error) {
      return showError(error, GROUP_ERROR_COPY);
    }
    // Lines the reviewer already changed by hand keep that choice; say so,
    // so a group that doesn't fully clear isn't a mystery.
    const notices = [
      ...(rejected.length
        ? [
            `${plural(rejected.length, "line wasn't", "lines weren't")} updated — review ${
              rejected.length === 1 ? "it" : "them"
            } individually.`
          ]
        : []),
      ...(skippedOverridden
        ? [
            `${plural(skippedOverridden, "line you changed", "lines you changed")} individually ${
              skippedOverridden === 1 ? "was" : "were"
            } left as you set ${skippedOverridden === 1 ? "it" : "them"}.`
          ]
        : [])
    ];
    return (
      !!notices.length &&
      setNotice({ severity: "warning", text: notices.join(" ") })
    );
  };

  const commitLine = async (lineRef) => {
    const key = lineKeyFor(lineRef);
    setCommittingKeys((current) => [...current, key]);
    const response = await commitSmartUploadLines({
      companyId,
      taskDocId,
      lines: [lineRef],
      confirmSlideoutPrompt: isPhysicalContext
    });
    const results = Array.isArray(response?.results)
      ? response.results
      : [
          {
            ...lineRef,
            outcome: "error",
            message:
              response?.message ||
              "We couldn't reach the server — please try again."
          }
        ];
    setCommitResults((current) => ({
      ...current,
      ...Object.fromEntries(
        results.map((result) => [lineKeyFor(result), result])
      )
    }));
    setCommittingKeys((current) => current.filter((item) => item !== key));
    return refresh();
  };

  const startBulkCommit = async () => {
    setIsStarting(true);
    setNotice(null);
    const response = await startCsvBulkCommit({
      companyId,
      taskDocId,
      expectedReadyCount: summary.readyCount,
      confirmSlideoutPrompt: isPhysicalContext
    });
    setIsStarting(false);
    if (response?.reason === "ready_count_changed") {
      setNotice({ severity: "warning", text: READY_COUNT_CHANGED_COPY });
      return refresh();
    }
    if (!response || response.error) {
      return setNotice({
        severity: "error",
        text:
          response?.message ||
          "We couldn't start confirming these lines — please try again."
      });
    }
    setStartedRunId(response.runId);
    return refresh();
  };

  const cancelRun = async () => {
    setIsCancelling(true);
    const response = await cancelCsvBulkCommit({
      companyId,
      taskDocId,
      runId
    });
    setIsCancelling(false);
    if (!response || response.error) {
      return showError(response, "We couldn't cancel — please try again.");
    }
    return refresh();
  };

  const recordCount = task.totalTasksProcessed ?? task.totalTasks ?? 0;
  const reviewNotice = reviewNoticeFor({ task, summary, recordCount });
  const isPreReview = isPreReviewImport(task, summary);
  const readyCount = summary?.readyCount || 0;
  const isConfirmDisabled =
    !canReview ||
    !readyCount ||
    isRunActive ||
    isStarting ||
    isStillReading(task, summary);
  const runOutcome = run && RUN_OUTCOMES[run.status];
  const showAttention =
    canReview &&
    (!!attention.groups.length ||
      !!attentionError ||
      !!summary?.attentionCount);

  const lineProps = {
    companyId,
    taskDocId,
    documentFamily,
    vendorOptions,
    closedYears,
    commitResults,
    refreshKey,
    isCommitting: !!committingKeys.length,
    onConfirmLine: (lineRef) => requestWithSlideout(() => commitLine(lineRef)),
    onLineChanged: refresh
  };

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xl">
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6">
              {TITLES[documentFamily] || "Review uploaded file"}
            </Typography>
            {summary && !isPreReview && (
              <Typography variant="caption" color="textSecondary">
                {summaryCaption(summary)}
              </Typography>
            )}
          </Box>
          <IconButton aria-label="Close review" onClick={onClose}>
            <FontAwesomeIcon icon={faXmark} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        {reviewNotice && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {reviewNotice}
          </Alert>
        )}
        {loadError && (
          <Alert
            severity="error"
            sx={{ mb: 1 }}
            action={
              <Button size="small" onClick={refresh}>
                Retry
              </Button>
            }
          >
            {loadError}
          </Alert>
        )}
        {runError && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            {runError}
          </Alert>
        )}
        {groupProgress && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Applying to{" "}
            {plural(groupProgress.remaining, "more line", "more lines")}…
          </Alert>
        )}
        {notice && (
          <Alert severity={notice.severity} sx={{ mb: 1 }}>
            {notice.text}
          </Alert>
        )}
        {isPhysicalContext && canReview && (
          <Typography variant="body2" color="textSecondary">
            These lines were uploaded to {contextEntityName}.
          </Typography>
        )}
        {isRunActive && run && (
          <RunProgress
            run={run}
            onCancel={cancelRun}
            isCancelling={isCancelling}
          />
        )}
        {runOutcome && (
          <Alert severity={runOutcome.severity} sx={{ mb: 1 }}>
            {runOutcome.text(run)}
          </Alert>
        )}
        {!summary && !loadError && !reviewNotice && (
          <CircularProgress size={24} />
        )}
        {summary && !isPreReview && !isStillReading(task, summary) && (
          <>
            {showAttention && (
              <CsvAttentionGroups
                groups={attention.groups}
                truncated={attention.truncated}
                error={attentionError}
                attentionCount={summary.attentionCount}
                onRetry={refresh}
                onGroupUpdate={applyToGroup}
                {...lineProps}
              />
            )}
            {!!summary.failedCount && (
              <CsvFailedLines
                companyId={companyId}
                taskDocId={taskDocId}
                documentFamily={documentFamily}
                failedCount={summary.failedCount}
                refreshKey={refreshKey}
                commitResults={commitResults}
                canRetry={canReview && !isRunActive}
                isCommitting={!!committingKeys.length}
                onRetryLine={lineProps.onConfirmLine}
              />
            )}
            {!!summary.erroredRowCount && (
              <CsvErroredRows
                companyId={companyId}
                taskDocId={taskDocId}
                documentFamily={documentFamily}
                erroredRowCount={summary.erroredRowCount}
                refreshKey={refreshKey}
                isRunActive={isRunActive}
                onRetried={refresh}
              />
            )}
            <CsvLinesBrowser
              companyId={companyId}
              taskDocId={taskDocId}
              documentFamily={documentFamily}
              summary={summary}
              closedYears={closedYears}
              refreshKey={refreshKey}
              canReview={canReview}
              isEnabled
              onGroupUpdate={applyToGroup}
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ gap: 1 }}>
        <Button onClick={onClose}>Close</Button>
        {canReview && (
          <Button
            variant="contained"
            color="secondary"
            disabled={isConfirmDisabled}
            sx={{ color: palette.common.white }}
            onClick={() => requestWithSlideout(startBulkCommit)}
          >
            Confirm {plural(readyCount, ...readyNounFor(documentFamily))}
          </Button>
        )}
      </DialogActions>
      {pendingAction && (
        <SmartUploadSlideoutPrompt
          entityName={contextEntityName}
          onCancel={() => setPendingAction(null)}
          onConfirm={confirmSlideout}
        />
      )}
    </Dialog>
  );
};

export default SmartUploadCsvReview;
